// Predefined directory field-mapping transformation chains ("presets").
//
// Each preset drops a ready-made chain of `{ regexp, change, flags }` rows onto a
// field-mapping line item's `transforms` array (see directory-fields.svelte).
//
// Values wrapped in double asterisks are TOKENS the backend interprets, not literal
// text: `**formatPhone**`, `**trim**`, `**flipNameComma**`, `**toUpperCase**`,
// `**toLowerCase**`, and `**space**` (a single space character — it needs a named
// token because a literal space is invisible in the input). Unwrapped values such as
// `.` or `-` are literal replacement text. Keep the asterisks exactly as written.
//
// Source of truth: the internal help doc "Domain Settings (Internal Use Only)" →
// "Auto-Sync & Directory Field Transformations". These chains are validated live by
// Implementation Engineers; transcribe them verbatim — token spelling, empty cells,
// and flag values are all load-bearing (they form a backend-consumed contract).

export const DIRECTORY_TRANSFORM_PRESETS = [
  // --- Phone (no standard phone format in AD) ---
  {
    id: 'phone-dots',
    group: 'phone',
    labelKey: 'label.TRANSFORM_PRESET_PHONE_DOTS',
    tipKey: 'label.TRANSFORM_PRESET_TIP_PHONE_DOTS',
    transforms: [
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '(.*)', change: '**formatPhone**', flags: '' },
      { regexp: '^.+**space**', change: '**trim**', flags: '' },
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '^.+**space**', change: '**trim**', flags: '' },
      { regexp: '[()-]', change: '.', flags: 'g' }
    ]
  },
  {
    id: 'phone-dashes',
    group: 'phone',
    labelKey: 'label.TRANSFORM_PRESET_PHONE_DASHES',
    tipKey: 'label.TRANSFORM_PRESET_TIP_PHONE_DASHES',
    transforms: [
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '(.*)', change: '**formatPhone**', flags: '' },
      { regexp: '^.+**space**', change: '**trim**', flags: '' },
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '^.+**space**', change: '**trim**', flags: '' },
      { regexp: '[()-]', change: '-', flags: 'g' }
    ]
  },
  {
    id: 'phone-parens',
    group: 'phone',
    labelKey: 'label.TRANSFORM_PRESET_PHONE_PARENS',
    tipKey: 'label.TRANSFORM_PRESET_TIP_PHONE_PARENS',
    // Row 3 has an empty `regexp` and there are no `g` flags — verbatim from the doc.
    transforms: [
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '(.*)', change: '**formatPhone**', flags: '' },
      { regexp: '', change: '**trim**', flags: '' },
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '^.+**space**', change: '(', flags: '' },
      { regexp: '[-]', change: ')**space**', flags: '' }
    ]
  },
  {
    id: 'phone-parens-plus1',
    group: 'phone',
    labelKey: 'label.TRANSFORM_PRESET_PHONE_PARENS_PLUS1',
    tipKey: 'label.TRANSFORM_PRESET_TIP_PHONE_PARENS_PLUS1',
    // Same chain as `phone-parens`, but row 5 prefixes the country code.
    transforms: [
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '(.*)', change: '**formatPhone**', flags: '' },
      { regexp: '', change: '**trim**', flags: '' },
      { regexp: 'x.*$', change: '**space**', flags: '' },
      { regexp: '^.+**space**', change: '+1 (', flags: '' },
      { regexp: '[-]', change: ')**space**', flags: '' }
    ]
  },
  // --- Office ---
  {
    id: 'office-starter',
    group: 'office',
    labelKey: 'label.TRANSFORM_PRESET_OFFICE_STARTER',
    tipKey: 'label.TRANSFORM_PRESET_TIP_OFFICE_STARTER',
    // Starter row: user duplicates it (02, 03, …) and fills each office address.
    transforms: [{ regexp: '(?<!\\d)01(?!\\d)', change: '', flags: 'g' }]
  },
  // --- Text ---
  {
    id: 'text-flip-name',
    group: 'text',
    labelKey: 'label.TRANSFORM_PRESET_TEXT_FLIP_NAME',
    tipKey: 'label.TRANSFORM_PRESET_TIP_TEXT_FLIP_NAME',
    transforms: [{ regexp: '(.*)', change: '**flipNameComma**', flags: '' }]
  },
  {
    id: 'text-uppercase',
    group: 'text',
    labelKey: 'label.TRANSFORM_PRESET_TEXT_UPPERCASE',
    tipKey: 'label.TRANSFORM_PRESET_TIP_TEXT_UPPERCASE',
    transforms: [{ regexp: '(.*)', change: '**toUpperCase**', flags: '' }]
  },
  {
    id: 'text-lowercase',
    group: 'text',
    labelKey: 'label.TRANSFORM_PRESET_TEXT_LOWERCASE',
    tipKey: 'label.TRANSFORM_PRESET_TIP_TEXT_LOWERCASE',
    transforms: [{ regexp: '(.*)', change: '**toLowerCase**', flags: '' }]
  },
  {
    id: 'text-trim',
    group: 'text',
    labelKey: 'label.TRANSFORM_PRESET_TEXT_TRIM',
    tipKey: 'label.TRANSFORM_PRESET_TIP_TEXT_TRIM',
    transforms: [{ regexp: '(.*)', change: '**trim**', flags: '' }]
  },
  {
    id: 'text-remove-all',
    group: 'text',
    labelKey: 'label.TRANSFORM_PRESET_TEXT_REMOVE_ALL',
    tipKey: 'label.TRANSFORM_PRESET_TIP_TEXT_REMOVE_ALL',
    // Template: user replaces the 'character' placeholder with their delimiter (e.g. -).
    transforms: [{ regexp: "('character'.*)", change: '', flags: '' }]
  }
];
